import React, { useEffect, useState, useCallback } from "react";
import orderApi from "../../../services/apiServices/orderApi";
import "./PaymentConfirmationPage.css";
import { setLoading } from "../../../services/redux/loadingSlice";
import { useDispatch } from "react-redux";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { useNavigate, useParams } from "react-router-dom";
import ArrowIcon from "../../../components/icons/ArrowIcon";

const PaymentConfirmationPage = () => {
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    // ----- helpers que não são hooks -----

    const checkStatus = (transaction) => {
        if (!transaction) return "Status indisponível";

        if (transaction.CodePaymentOption === "PIX") {
            if (transaction.StAutorization === "AWAITING_PAYMENT") return "Aguardando Pagamento";
            if (transaction.StAutorization === "PAY" || transaction.StAutorization === "APPROVED")
                return "Pedido Pago";
            return "Falha no Pagamento";
        }

        if (transaction.StAutorization === "APPROVED" || transaction.StAutorization === "PAY")
            return "Pedido Pago";

        return "Pedido não autorizado";
    };

    const showCardDetails = (transaction) => {
        if (!transaction) return false;
        return transaction.CodePaymentOption !== "PIX";
    };

    const getPaymentMethod = (transaction) => {
        if (!transaction) return "Método de pagamento não identificado";

        if (transaction.CodePaymentOption === "CREDIT_CARD") return "Cartão de Crédito";
        if (transaction.CodePaymentOption === "PIX") return "Pix";

        return "Método de pagamento não identificado";
    };

    const getStatusClass = (transaction) => {
        const status = checkStatus(transaction);

        if (status === "Pedido Pago") {
            return "orders-list-status-paid";
        }

        if (status === "Aguardando Pagamento") {
            return "orders-list-status-pending"; // amarelo
        }

        return "orders-list-status-expired"; // vermelho
    };

    const showQRCode = (transaction) => {
        const status = checkStatus(transaction);
        return status === "Aguardando Pagamento";
    };

    const [copiado, setCopiado] = useState(false);

    const copy = async (value) => {
    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(value);
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = value;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
        console.error("Erro ao copiar", err);
    }
    };

    // ----- HOOK: fetchOrder com useCallback (tem que vir ANTES dos useEffect) -----

    const fetchOrder = useCallback(
        async (showLoader = true) => {
            try {
                if (showLoader) {
                    dispatch(setLoading(true));
                }

                const response = await orderApi.getOrderByNumber(id, {
                    include:
                        "Customer,Customer.CustomerType,Customer.User,Customer.User.Address,Customer.User.Address.City,OrderHistory,OrderTransaction"
                });

                setOrder(response);
                setError(null);
            } catch (err) {
                console.error("Error fetching order:", err);
                setError("Erro ao carregar pedido. Tente novamente.");
            } finally {
                if (showLoader) {
                    dispatch(setLoading(false));
                }
            }
        },
        [dispatch, id]
    );

    // ----- HOOK: carregamento inicial -----

    useEffect(() => {
        fetchOrder(); // primeira carga
    }, [fetchOrder]);

    // ----- HOOK: polling enquanto estiver aguardando pagamento -----

    useEffect(() => {
        if (!order) return;

        const transaction = order.OrderTransaction
            ?.slice()
            .sort((a, b) => new Date(b.Created) - new Date(a.Created))[0];

        if (!transaction) return;

        const status = checkStatus(transaction);

        if (status !== "Aguardando Pagamento") {
            return;
        }

        const intervalId = setInterval(() => {
            fetchOrder(false); // atualiza sem mostrar loader
        }, 10000);

        return () => clearInterval(intervalId);
    }, [order, fetchOrder]);

    // ----- returns condicionais (depois de TODOS os hooks) -----

    if (error) {
        return <p className="payment-confirmation-error-message">{error}</p>;
    }

    if (!order) {
        return <p className="payment-confirmation-loading-message">Carregando...</p>;
    }

    // ----- render normal -----

    const items = JSON.parse(order.StItems);
    const address = order.Customer?.User?.Address?.[0];
    const transaction = order.OrderTransaction
        ?.slice()
        .sort((a, b) => new Date(b.Created) - new Date(a.Created))[0];

    return (
        <div className="payment-confirmation-page">
            <span
                className="div-center clickable link small-font right-component"
                onClick={() => navigate("/")}
            >
                <ArrowIcon size={12} /> Voltar para a lista de compras
            </span>
            <PageTitle title="Confirmação de Pagamento" />

            <div className="status-order">
                <p className={`orders-list-status-label ${getStatusClass(transaction)}`}>
                    {checkStatus(transaction)}
                </p>
            </div>

            <div className="payment-div">
                <h3 className="title">Detalhes do Pagamento</h3>
                <div className="payment-details">
                    <p>
                        <strong>Método:</strong> {getPaymentMethod(transaction)}
                    </p>
                    <p>
                        <strong>Status:</strong> {checkStatus(transaction)}
                    </p>
                    {showCardDetails(transaction) && (
                        <p>
                            <strong>Parcelas:</strong> {transaction.Installments}x
                        </p>
                    )}
                    <p>
                        <strong>Valor Pago:</strong> R${" "}
                        {(transaction.VlPaid ?? 0).toFixed(2).replace(".", ",")}
                    </p>
                    {showCardDetails(transaction) && (
                        <p>
                            <strong>NSU:</strong> {transaction.Nsu}
                        </p>
                    )}
                </div>
            </div>

            {showQRCode(transaction) && (
                <div className="payment-div">
                    <h3 className="title">QR Code</h3>
                    <div className="payment-details" style={{ textAlign: "center" }}>
                        <img src={order.LinkCode} alt="QR Code" />
                    </div>
                    <div className="margin-top-default">
                        <div className="text-center pix-text-div">
                            Tempo para efetuar o pagamento: <strong>15 minutos</strong>
                        </div>
                        <div className="margin-top-default copy-button-wrapper">
                            <button type="button" className="main-button" onClick={() => copy(transaction.PixCopyCode)}>
                                {copiado ? "Código PIX copiado ✔️" : "Copiar código PIX"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="payment-div">
                <div className="payment-summary">
                    <h3 className="title">Resumo da compra</h3>
                    <p>
                        <strong>{items.length} itens</strong>
                    </p>
                    <p className="payment-summary-total">
                        <strong>Total do pedido</strong>
                        <span>R$ {order.VlnetPrice.toFixed(2).replace(".", ",")}</span>
                    </p>
                </div>
            </div>

            <div className="payment-div">
                <h3 className="title">Itens</h3>
                <div className="payment-items">
                    {items.map((item, index) => (
                        <div key={index} className="payment-item">
                            {item.ISBN ? (
                                <img
                                    src={`https://pwm-public-product-pro.s3.amazonaws.com/${item.ISBN}-large.webp`}
                                    alt="Produto"
                                    className="order-item-image"
                                />
                            ) : (
                                <img
                                    src="https://pwm-public-static-pro.s3.amazonaws.com/static/img/no-cover.png"
                                    alt="Produto"
                                    className="order-item-image"
                                />
                            )}
                            <div className="payment-item-details">
                                <h4>{item.Description}</h4>
                                <p>
                                    <strong>Qtd.</strong> {item.Quantity}{" "}
                                    <span>
                                        R$ {item.UnitPrice.toFixed(2).replace(".", ",")}
                                    </span>
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {address && (
                <div className="payment-div">
                    <h3 className="title">Endereço</h3>
                    <div className="payment-address-info">
                        <p>
                            {address?.StStreet}, {address?.NrBuildIngsite}
                            {address?.StComplement && `, ${address?.StComplement}`} -{" "}
                            {address?.StNeighborhood}
                        </p>
                        <p>
                            {address?.City
                                ? `${address?.City.Name} - ${address?.City.State}`
                                : ""}
                        </p>
                        <p>{address?.StZipCode}</p>
                    </div>
                </div>
            )}

            <div className="payment-div">
                <h3 className="title">Histórico</h3>
                <div className="payment-history">
                    {order.OrderHistory.map((history, index) => (
                        <div key={index} className="history-item">
                            <p>
                                <strong>{history.Title}</strong>
                            </p>
                            <p>{history.Description}</p>
                            <p>{new Date(history.Created).toLocaleString("pt-BR")}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PaymentConfirmationPage;
